// Build and download a structured JSON report of a comparison session.
// Pure client-side — no backend. Uses Blob + URL.createObjectURL.

/**
 * Shape of a single region entry in the exported report.
 * @typedef {object} ReportRegion
 * @property {string} countryCode
 * @property {string} countryName
 * @property {boolean} available
 * @property {number|null} averageUserRating
 * @property {number|null} userRatingCount
 * @property {number|null} price
 * @property {string|null} formattedPrice
 * @property {number|null} popularityScore
 * @property {number|null} popularityRank
 * @property {boolean} isOpportunity
 * @property {string|null} storeUrl
 */

/**
 * Shape of the exported report document.
 * @typedef {object} MarketReport
 * @property {string} tool
 * @property {string} exportedAt  ISO-8601 timestamp
 * @property {object} app
 * @property {ReportRegion[]} regions
 * @property {object} summary
 */

/**
 * Build a MarketReport object from the current comparison rows + app info.
 *
 * @param {object} app - Raw iTunes result for the compared app (trackId, trackName, sellerName, …).
 * @param {import('./normalizeApp.js').RegionRow[]} rows - Annotated rows (with popularityScore, opportunity).
 * @param {string} exportedAt - ISO-8601 timestamp string (injected so tests stay deterministic).
 * @returns {MarketReport}
 */
export function buildReport(app, rows, exportedAt) {
  const available = rows.filter((r) => r.available && !r.error);

  const topByPopularity = [...available]
    .sort((a, b) => (b.popularityScore ?? -1) - (a.popularityScore ?? -1))
    .slice(0, 5)
    .map((r) => r.countryCode);

  const opportunities = available
    .filter((r) => r.opportunity?.isOpportunity)
    .map((r) => r.countryCode);

  const regions = rows.map((r) => ({
    countryCode: r.countryCode,
    countryName: r.countryName ?? r.countryCode,
    available: r.available && !r.error,
    averageUserRating: r.averageUserRating ?? null,
    userRatingCount: r.userRatingCount ?? null,
    price: r.price ?? null,
    formattedPrice: r.formattedPrice ?? null,
    popularityScore: r.popularityScore ?? null,
    popularityRank: r.popularityRank ?? null,
    isOpportunity: r.opportunity?.isOpportunity ?? false,
    storeUrl: r.storeUrl ?? null,
  }));

  return {
    tool: 'App Radar',
    exportedAt,
    app: {
      trackId: app?.trackId ?? null,
      trackName: app?.trackName ?? null,
      sellerName: app?.sellerName ?? null,
      bundleId: app?.bundleId ?? null,
      artworkUrl: app?.artworkUrl100 ?? app?.artworkUrl60 ?? null,
    },
    regions,
    summary: {
      totalStorefronts: rows.length,
      availableStorefronts: available.length,
      topMarketsByPopularity: topByPopularity,
      opportunityMarkets: opportunities,
    },
  };
}

/**
 * Serialize a report to a JSON Blob and trigger a browser download.
 *
 * @param {MarketReport} report
 * @param {string} filename - e.g. "yelp-2026-06-17.json"
 */
export function downloadReport(report, filename) {
  const json = JSON.stringify(report, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build a download filename for a report.
 * @param {string|null} appName
 * @param {string} dateStr - e.g. "2026-06-17"
 * @returns {string}
 */
export function buildReportFilename(appName, dateStr) {
  const slug = appName
    ? appName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
    : 'app';
  return `${slug}-market-report-${dateStr}.json`;
}
